import { useState } from 'react';
import { Link } from 'react-router-dom';
import { theme } from '../theme';
import { useIsMobile } from '../hooks/useMediaQuery';
import { PRIVACY_SECTIONS } from './legal/Privacidad';

// El modal muestra la Política de Privacidad / Habeas Data (autorización informada de
// tratamiento de datos, Ley 1581 de 2012) y, al aceptar, el usuario también acepta las
// Condiciones del Servicio (ver /terminos). Fuente única: PRIVACY_SECTIONS.
const HABEAS_TEXT = PRIVACY_SECTIONS;

function HabeasModal({ token, onAccept, onReject }) {
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleAccept = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/auth/accept-habeas', {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.message || 'Error al guardar. Intenta de nuevo.');
        return;
      }
      onAccept();
    } catch {
      setError('Error de conexión. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0,0,0,0.85)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 500,
      padding: '1rem',
    }}>
      <div style={{
        backgroundColor: theme.colors.surface,
        borderRadius: '14px',
        border: `1px solid ${theme.colors.border}`,
        width: '100%',
        maxWidth: '560px',
        maxHeight: '85vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 8px 48px rgba(0,0,0,0.6)',
      }}>
        {/* Header */}
        <div style={{
          padding: isMobile ? '1.25rem 1.25rem 0.85rem' : '1.5rem 2rem 1rem',
          borderBottom: `1px solid ${theme.colors.border}`,
          flexShrink: 0,
        }}>
          <h2 style={{ color: theme.colors.primary, fontSize: '1.2rem', marginBottom: '0.3rem' }}>
            Condiciones del Servicio y Tratamiento de Datos
          </h2>
          <p style={{ color: theme.colors.textMuted, fontSize: '0.82rem' }}>
            Ley 1581 de 2012 — Habeas Data · Colombia
          </p>
        </div>

        {/* Scrollable content */}
        <div style={{
          padding: isMobile ? '1.2rem 1.25rem' : '1.2rem 2rem',
          overflowY: 'auto',
          flex: 1,
        }}>
          <p style={{ color: theme.colors.text, fontSize: '0.9rem', lineHeight: '1.7', marginBottom: '1.2rem' }}>
            Antes de continuar, debes leer y aceptar las{' '}
            <Link to="/terminos" target="_blank" rel="noopener noreferrer" style={{ color: theme.colors.primary, fontWeight: 700 }}>
              Condiciones del Servicio
            </Link>{' '}
            y autorizar el tratamiento de tus datos personales por parte de{' '}
            <strong style={{ color: theme.colors.primary }}>New horizont start</strong>, conforme a la
            siguiente Política de Privacidad (también disponible en{' '}
            <Link to="/privacidad" target="_blank" rel="noopener noreferrer" style={{ color: theme.colors.primary, fontWeight: 700 }}>
              /privacidad
            </Link>).
          </p>

          {HABEAS_TEXT.map((section, i) => (
            <div key={i} style={{ marginBottom: '1.1rem' }}>
              <h3 style={{ color: theme.colors.primary, fontSize: '0.88rem', fontWeight: '700', marginBottom: '0.35rem' }}>
                {section.title}
              </h3>
              <p style={{ color: theme.colors.text, fontSize: '0.88rem', lineHeight: '1.65', margin: 0 }}>
                {section.content}
              </p>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div style={{
          padding: isMobile ? '1.2rem 1.25rem' : '1.2rem 2rem',
          borderTop: `1px solid ${theme.colors.border}`,
          flexShrink: 0,
        }}>
          {error && (
            <p style={{ color: theme.colors.error, fontSize: '0.85rem', textAlign: 'center', marginBottom: '0.8rem' }}>
              {error}
            </p>
          )}
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button
              onClick={onReject}
              disabled={loading}
              style={{
                flex: 1,
                padding: '11px',
                backgroundColor: 'transparent',
                border: `1px solid ${theme.colors.error}`,
                borderRadius: '7px',
                color: theme.colors.error,
                fontSize: '0.9rem',
                fontWeight: '600',
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              Rechazar
            </button>
            <button
              onClick={handleAccept}
              disabled={loading}
              style={{
                flex: 1,
                padding: '11px',
                backgroundColor: loading ? theme.colors.border : theme.colors.primary,
                border: 'none',
                borderRadius: '7px',
                color: theme.colors.background,
                fontSize: '0.9rem',
                fontWeight: '700',
                cursor: loading ? 'not-allowed' : 'pointer',
              }}
            >
              {loading ? 'Guardando...' : 'Aceptar y continuar'}
            </button>
          </div>
          <p style={{ color: theme.colors.textMuted, fontSize: '0.78rem', textAlign: 'center', marginTop: '0.8rem' }}>
            Al aceptar confirmas que has leído y aceptas las Condiciones del Servicio y la Política de
            Privacidad. Si rechazas, no podrás acceder a la plataforma.
          </p>
        </div>
      </div>
    </div>
  );
}

export default HabeasModal;
